import React, { useEffect, useMemo } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import UserService from '../../../services/UserService';
import { Pie } from '@ant-design/charts';
import IconNoData from '../../../assets/Images/icons/icon-no-data';

export default function Ropa({ summary }) {
    const { t } = useTranslation();
    const [dataPie, setDataPie] = useState([]);
    const totalValue = dataPie.reduce((acc, item) => acc + item.value, 0);

    useEffect(() => {
        setDataPie([
            {
                type: t('Draft'),
                value: summary?.ropaDraft || 0,
            },
            {
                type: t('Waiting for Review'),
                value: summary?.ropaWaitingForReview || 0,
            },
            {
                type: t('Reviewed'),
                value: summary?.ropaReviewed || 0,
            },
            {
                type: t('Revise'),
                value: summary?.ropaRevise || 0,
            },
        ]);
    }, [summary, t]);

    // Role
    const role = UserService.role().toLowerCase();
    const admin = ['admin', 'auditor', 'super admin'].includes(role);

    const config = useMemo(() => {
        return {
            data: dataPie,
            angleField: 'value',
            colorField: 'type',
            color: ({ type }) => {
                switch (type) {
                    case t('Draft'):
                        return '#E9DEFA';
                    case t('Waiting for Review'):
                        return '#9E86FF';
                    case t('Reviewed'):
                        return '#826AF9';
                    case t('Revise'):
                        return '#EFA6FF';
                    default:
                        return '#E9DEFA';
                }
            },
            width: 200,
            height: 200,
            radius: 1,
            innerRadius: 0.6,
            interactions: [
                {
                    type: 'element-active',
                },
            ],
            statistic: {
                title: {
                    content: t('Total'),
                    style: {
                        fontSize: '12px',
                    },
                },
                content: {
                    content: totalValue > 0 ? totalValue.toString() : '0',
                },
            },
        };
    }, [dataPie, t, totalValue]);

    const draftPercent = (
        (summary?.ropaDraft / summary?.ropaTotal) *
        100
    ).toFixed(2);

    const waitingForReviewPercent = (
        (summary?.ropaWaitingForReview / summary?.ropaTotal) *
        100
    ).toFixed(2);

    const reviewedPercent = (
        (summary?.ropaReviewed / summary?.ropaTotal) *
        100
    ).toFixed(2);

    const revisePercent = (
        (summary?.ropaRevise / summary?.ropaTotal) *
        100
    ).toFixed(2);

    return (
        <div>
            <div className="flex space-x-2 items-center pb-4 justify-between">
                <div className="font-semibold text-xl">
                    {admin
                        ? t('RoPA in your organization')
                        : t('RoPA in your department')}
                </div>
            </div>
            {summary ? (
                <>
                    <Pie {...config} />
                    <div className="flex justify-between pt-4 pb-2 ">
                        <div className="flex items-center">
                            <Icon color="#E9DEFA" />
                            <div className="pl-2">{t('Draft')}</div>
                        </div>
                        <div className="text-gray-500">
                            {summary?.ropaDraft} (
                            {isNaN(draftPercent) ? 0 : draftPercent}%)
                        </div>
                    </div>

                    <div className="flex justify-between pb-2">
                        <div className="flex items-center">
                            <Icon color="#9E86FF" />
                            <div className="pl-2">
                                {t('Waiting for Review')}
                            </div>
                        </div>
                        <div className="text-gray-500">
                            {summary?.ropaWaitingForReview} (
                            {isNaN(waitingForReviewPercent)
                                ? 0
                                : waitingForReviewPercent}
                            %)
                        </div>
                    </div>

                    <div className="flex justify-between pb-2">
                        <div className="flex items-center">
                            <Icon color="#826AF9" />
                            <div className="pl-2">{t('Reviewed')}</div>
                        </div>
                        <div className="text-gray-500">
                            {summary?.ropaReviewed} (
                            {isNaN(reviewedPercent) ? 0 : reviewedPercent}
                            %)
                        </div>
                    </div>

                    <div className="flex justify-between pb-2">
                        <div className="flex items-center">
                            <Icon color="#EFA6FF" />
                            <div className="pl-2">{t('Revise')}</div>
                        </div>
                        <div className="text-gray-500">
                            {summary?.ropaRevise} (
                            {isNaN(revisePercent) ? 0 : revisePercent}%)
                        </div>
                    </div>

                    <div
                        className="pt-2 flex justify-between"
                        style={{ borderTop: '0.5px solid #E9EBF1' }}
                    >
                        <div className="font-semibold">{t('Total')}</div>
                        <div className="text-gray-500">
                            {summary?.ropaTotal} (100%)
                        </div>
                    </div>
                </>
            ) : (
                <div className="text-center text-sm space-y-2 my-32">
                    <IconNoData />
                    <p className="text-[#65707A] text-[14px]">
                        {t('common.noData')}
                    </p>
                </div>
            )}
        </div>
    );
}

const Icon = ({ color }) => (
    <svg
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <circle cx="6" cy="6" r="6" fill={color} />
    </svg>
);
